<template>
  <div class="slide slide-focus-by-goal">
    <!-- Header -->
    <div class="slide-header">
      <h2 class="slide-title">{{ data.title || $t('reports.quarterly.focus_alignment.default_title') }}</h2>
      <div class="slide-subtitle">{{ data.subtitle || $t('reports.quarterly.focus_alignment.default_subtitle') }}</div>
    </div>

    <!-- Goals List -->
    <div class="goals-list">
      <div
        v-for="(goal, index) in (data.goals || []).slice(0, 5)"
        :key="index"
        class="goal-row"
        :style="{ animationDelay: `${index * 0.08}s` }"
      >
        <!-- Rank Badge -->
        <div class="rank-badge" :style="{ background: getGoalColor(index) }">
          {{ index + 1 }}
        </div>

        <!-- Goal Info -->
        <div class="goal-info">
          <div class="goal-name">{{ goal.goalName }}</div>
          <div class="goal-team">{{ goal.teamName }}</div>
        </div>

        <!-- Progress Section -->
        <div class="progress-section">
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: getGoalBarWidth(goal) + '%', background: getGoalColor(index) }"
            ></div>
          </div>
          <div class="progress-labels">
            <span class="label-items">{{ formatNumber(goal.focusItems) }} {{ $t('reports.quarterly.focus_alignment.items') }}</span>
            <span class="label-completion" :style="{ color: getGoalColor(index) }">
              {{ goal.completedItems || 0 }} {{ $t('reports.quarterly.focus_alignment.done') }} / {{ (goal.focusItems || 0) - (goal.completedItems || 0) }} {{ $t('reports.quarterly.focus_alignment.open') }}
            </span>
          </div>
        </div>

        <!-- People Count -->
        <div class="people-section">
          <div class="people-count">{{ goal.peopleCount }}</div>
          <div class="people-label">{{ $t('reports.quarterly.focus_alignment.people') }}</div>
        </div>

        <!-- Top Focus Items -->
        <div class="focus-items-section" v-if="goal.topItems && goal.topItems.length > 0">
          <div
            v-for="(item, itemIndex) in goal.topItems.slice(0, 2)"
            :key="itemIndex"
            class="focus-item"
          >
            <span class="focus-bullet" :style="{ background: getGoalColor(index) }"></span>
            <span class="focus-text">{{ truncateText(item.item, 36) }}</span>
            <span v-if="item.partnerCount > 0" class="partner-count">+{{ item.partnerCount }}</span>
          </div>
        </div>
      </div>

      <!-- Unmatched Row -->
      <div v-if="data.unalignedItems > 0" class="goal-row unmatched-row">
        <div class="rank-badge unmatched-badge">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
            <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>

        <div class="goal-info">
          <div class="goal-name unmatched-name">{{ $t('reports.quarterly.focus_alignment.unmatched_items') }}</div>
          <div class="goal-team">{{ $t('reports.quarterly.focus_alignment.not_linked') }}</div>
        </div>

        <div class="progress-section">
          <div class="progress-bar unmatched-bar">
            <div
              class="progress-fill unmatched-fill"
              :style="{ width: getUnmatchedBarWidth() + '%' }"
            ></div>
          </div>
          <div class="progress-labels">
            <span class="label-items unmatched-text">{{ formatNumber(data.unalignedItems) }} {{ $t('reports.quarterly.focus_alignment.items') }}</span>
          </div>
        </div>

        <div class="people-section">
          <div class="people-count unmatched-text">—</div>
          <div class="people-label">{{ $t('reports.quarterly.focus_alignment.people') }}</div>
        </div>

        <div class="focus-items-section empty-items">
          <span class="empty-hint">{{ $t('reports.quarterly.focus_alignment.consider_linking') }}</span>
        </div>
      </div>

      <!-- Unknown Row: focus cannot be assessed until goals exist -->
      <div v-if="data.unknownItems > 0" class="goal-row unmatched-row unknown-row">
        <div class="rank-badge unmatched-badge">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
            <path d="M9.5 9a2.5 2.5 0 1 1 3.6 2.25c-.8.42-1.1.9-1.1 1.75M12 17h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>

        <div class="goal-info">
          <div class="goal-name unmatched-name">{{ $t('reports.quarterly.focus_alignment.unknown_items') }}</div>
          <div class="goal-team">{{ $t('reports.quarterly.focus_alignment.no_goals_set') }}</div>
        </div>

        <div class="progress-section">
          <div class="progress-bar unmatched-bar"></div>
          <div class="progress-labels">
            <span class="label-items unmatched-text">{{ formatNumber(data.unknownItems) }} {{ $t('reports.quarterly.focus_alignment.items') }}</span>
          </div>
        </div>

        <div class="people-section">
          <div class="people-count unmatched-text">—</div>
          <div class="people-label">{{ $t('reports.quarterly.focus_alignment.people') }}</div>
        </div>

        <div class="focus-items-section empty-items">
          <span class="empty-hint">{{ $t('reports.quarterly.focus_alignment.set_goals_to_assess') }}</span>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="(!data.goals || data.goals.length === 0) && !data.unalignedItems && !data.unknownItems" class="empty-state">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" class="empty-icon">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div class="empty-text">{{ $t('reports.quarterly.focus_alignment.empty_text') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FocusAlignmentSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  computed: {
    maxFocusItems() {
      const goalMax = this.data.goals && this.data.goals.length > 0
        ? Math.max(...this.data.goals.map(g => g.focusItems || 0))
        : 0;
      return Math.max(goalMax, this.data.unalignedItems || 0, this.data.unknownItems || 0, 1);
    }
  },
  methods: {
    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    getGoalBarWidth(goal) {
      return ((goal.focusItems || 0) / this.maxFocusItems) * 100;
    },
    getUnmatchedBarWidth() {
      return ((this.data.unalignedItems || 0) / this.maxFocusItems) * 100;
    },
    getGoalColor(index) {
      const colors = [
        '#059669', // emerald
        '#7c3aed', // violet
        '#3B82F6', // blue
        '#06B6D4', // cyan
        '#F59E0B', // amber
      ];
      return colors[index % colors.length];
    },
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-focus-by-goal {
  @extend .slide-base;
  background: $paper;
  padding: 80px;
  display: flex;
  flex-direction: column;
}

.slide-header {
  margin-bottom: 48px;
}

.slide-title {
  @extend .serif-headline;
  font-size: 56px;
  color: $ink;
  margin: 0;
}

.slide-subtitle {
  font-family: $font-body;
  font-size: 20px;
  color: $ink-muted;
  margin-top: 12px;
}

.goals-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  max-width: 1700px;
  margin: 0 auto;
  width: 100%;
}

.goal-row {
  display: grid;
  grid-template-columns: 56px 280px 1fr 100px 320px;
  gap: 28px;
  align-items: center;
  background: #fff;
  border-radius: 20px;
  padding: 24px 32px;
  box-shadow: $shadow-soft;
  animation: slideInRow 0.5s $ease-out-quart backwards;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(6px);
    box-shadow: $shadow-medium;
  }

  &.unmatched-row {
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    border: 1px dashed rgba(#f59e0b, 0.3);

    &:hover {
      transform: translateX(6px);
    }
  }

  &.unknown-row {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-color: rgba(#64748b, 0.3);

    .unmatched-badge {
      color: #64748b;
      border-color: #64748b;
    }

    .unmatched-name,
    .unmatched-text {
      color: #475569;
    }

    .unmatched-bar {
      background: rgba(#64748b, 0.12);
    }
  }
}

.rank-badge {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: $font-body;
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;

  &.unmatched-badge {
    background: transparent;
    color: #f59e0b;
    border: 2px dashed #f59e0b;
  }
}

.goal-info {
  min-width: 0;
}

.goal-name {
  font-family: $font-body;
  font-size: 20px;
  font-weight: 600;
  color: $ink;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.unmatched-name {
    color: #b45309;
  }
}

.goal-team {
  font-family: $font-body;
  font-size: 14px;
  color: $ink-muted;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.progress-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-bar {
  height: 12px;
  background: rgba($ink, 0.06);
  border-radius: 6px;
  overflow: hidden;

  &.unmatched-bar {
    background: rgba(#f59e0b, 0.15);
  }
}

.progress-fill {
  height: 100%;
  border-radius: 6px;
  transition: width 0.6s $ease-out-expo;

  &.unmatched-fill {
    background: linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%);
  }
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  font-family: $font-body;
  font-size: 13px;
}

.label-items {
  font-weight: 600;
  color: $ink;

  &.unmatched-text {
    color: #b45309;
  }
}

.label-completion {
  font-weight: 500;
}

.people-section {
  text-align: center;
}

.people-count {
  font-family: $font-body;
  font-size: 32px;
  font-weight: 700;
  color: $violet-600;
  line-height: 1;
  margin-bottom: 4px;

  &.unmatched-text {
    color: $ink-subtle;
  }
}

.people-label {
  font-family: $font-body;
  font-size: 12px;
  color: $ink-muted;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.focus-items-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 20px;
  border-left: 2px solid rgba($ink, 0.06);

  &.empty-items {
    justify-content: center;
  }
}

.focus-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.focus-bullet {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.focus-text {
  flex: 1;
  font-family: $font-body;
  font-size: 13px;
  color: $ink-muted;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.partner-count {
  font-family: $font-mono;
  font-size: 11px;
  color: $violet-500;
  background: rgba($violet-500, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.empty-hint {
  font-family: $font-body;
  font-size: 13px;
  color: #b45309;
  font-style: italic;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px;
  color: $ink-subtle;
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.5;
}

.empty-text {
  font-family: $font-body;
  font-size: 20px;
}

@keyframes slideInRow {
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
